import { Modal, UI, Utils } from 'common';
import includes from 'lodash/includes';
import isString from 'lodash/isString';
import React, { PureComponent } from 'react';
import AccountCommunication from '../utils/AccountCommunication';
import { TermsModal } from './TermsModal.component';

const {
	Footer,
	SaveConfirmation,
	Section,
	SectionBody,
	WrappedButton,
	WrappedButtonType,
	WrappedInputTextWithLabel,
	WrappedInputTextareaWithLabel,
	AdlSocialButton,
} = UI;
const { Session, Validation, Logger, withLayoutAndModalContexts } = Utils;
const { ModalButtonActionType } = Modal;

class ReferAFriend extends PureComponent {
	constructor(props) {
		super(props);

		Session.setFormStateDirty(false);

		// this function runs as soon as the SDK has completed loading
		// TODO: revisit to see if the app ID needs to be moved to the registry to be retrieved from Settings
		window.fbAsyncInit = () => {
			// eslint-disable-next-line no-unused-expressions
			// eslint-disable-next-line no-undef
			FB.init({
				appId: Session.getSettings().fbAppId,
				status: true,
				cookie: true,
				xfbml: true,
			});
		};

		const { customerDetails = {} } = Session.getAccountInfo();
		const name = customerDetails.address?.name || '';
		const firstName = name.split(' ')[0] || name;
		const senderAddress = customerDetails.address?.addressLine1 || '';
		const senderCity = customerDetails.address?.city || '';
		const senderState = customerDetails.address?.stateProvince || '';
		const senderZip = customerDetails.address?.postalCode || '';
		this.state = {
			firstName,
			yourName: name,
			senderAddress,
			senderCity,
			senderState,
			senderZip,
			message: '',
			recipientEmail: '',
			friendCode: '',
			clean: true,
			validations: {
				// 0 = untouched, -1 = failed, 1 = valid
				yourName: 1, // these are initially valid since we've retrieved them from Session
				message: 1,
				recipientEmail: 0,
			},
			errorMessages: {
				yourName: '',
				message: '',
				recipientEmail: '',
			},
			isSent: false, // true if the email has sent successfully
			copied: false,
		};
	}

	async componentDidMount() {
		const {
			modalContext: { showError },
		} = this.props;

		// append FB SDK script to this component's fb-root ID on mount
		(() => {
			const e = document.createElement('script');
			e.async = true;
			e.src = `${document.location.protocol}//connect.facebook.net/en_US/all.js`;
			document.getElementById('fb-root').appendChild(e);
		})();

		const response = await AccountCommunication.getReferAFriendData();

		if (!AccountCommunication.isSuccessful(response)) {
			return showError({ response });
		}

		const messageTemplate = `I use Stamps.com for my mailing and shipping needs and you should try it out.  Go to www.stamps.com/tell and use this code to sign up ${response.data}.`;

		this.setState({
			friendCode: response.data,
			message: messageTemplate,
		});
	}

	/**
	 * Run validation for the field if name is passed in the arguments. If name is `null`,
	 * it runs the validation for the entire form.
	 *
	 * @param {string} name (Optional) The name of the field to run validation.
	 * @param {string} value (Optional) The value of the field.
	 * @return {object}
	 */
	validate = (name, value) => {
		const { errorMessages, message, recipientEmail, validations, yourName } =
			this.state;

		// build array for fields to run validation
		const fields = isString(name)
			? [[name, value]]
			: [
					['yourName', yourName],
					['message', message],
					['recipientEmail', recipientEmail],
				];

		// run validation
		const validationResult = fields.reduce(
			(_result, [fName, fValue]) => {
				if (fName === 'yourName') {
					return {
						errorMessages: {
							...errorMessages,
							yourName: !fValue
								? 'Your Name is required.'
								: fValue.length > 100
									? 'Your Name cannot exceed 100 characters.'
									: '',
						},
						validations: {
							...validations,
							yourName: !fValue || fValue.length > 100 ? -1 : 1,
						},
					};
				} else if (fName === 'message') {
					return {
						errorMessages: {
							...errorMessages,
							message: !fValue ? 'Message is required.' : '',
						},
						validations: {
							...validations,
							message: !fValue ? -1 : 1,
						},
					};
				} else if (fName === 'recipientEmail') {
					const recipientEmailError = this.validateRecipientEmail(fValue);

					return {
						errorMessages: {
							...errorMessages,
							recipientEmail: recipientEmailError,
						},
						validations: {
							...validations,
							recipientEmail: recipientEmailError !== '' ? -1 : 1,
						},
					};
				}
				return _result;
			},
			{ errorMessages, validations }
		);

		// update UI for validation error
		this.setState({
			errorMessages: {
				...validationResult.errorMessages,
			},
			validations: {
				...validationResult.validations,
			},
		});

		return validationResult;
	};

	/**
	 * Validate Recipient Email Address value.
	 * @param {string} recipientEmail
	 * @return {string}
	 */
	validateRecipientEmail = recipientEmail => {
		// build an array of recipient email
		const recipientArray = recipientEmail
			.split(',')
			.map(email => email.trim())
			.filter(email => email);

		// return an error message if no recipient email is entered
		if (recipientArray.length === 0) {
			return 'Recipient Email Address is required.';
		}

		// check if any of the recipient email is invalid
		return recipientArray.reduce(
			(result, email) =>
				!Validation.isValidEmail(email)
					? 'One of the email addresses is invalid'
					: result,
			''
		);
	};

	onShareFacebookClick = e => {
		e.preventDefault();
		const { friendCode } = this.state;

		// eslint-disable-next-line no-undef
		FB.ui({
			method: 'feed',
			link: `http://www.stamps.com/tellafriend/landing/?source=si10190196&taf=${friendCode}`,
		});
		// TODO: splunk logging
	};

	onShareTwitterClick = () => {
		const { friendCode } = this.state;
		const link = `http://twitter.com/intent/tweet?url=http%3A%2F%2Fwww.stamps.com%2Ftell&related=stampscom&text=I%20use%20Stamps.com%20and%20you%20should%20try%20it%20out.%20Use%20this%20code%20to%20sign%20up,%20${friendCode},%20and%20you%20will%20get%20a%20$100%20offer.`;
		const params = `scrollbars=no,resizable=no,status=no,location=no,toolbar=no,menubar=no,
		width=600,height=300,left=200,top=200`;

		window.open(link, 'Share Twitter', params);
		// TODO: splunk logging
	};

	onFieldChange = e => {
		const { value, name } = e.target;

		this.setState({
			[name]: value,
			clean: false,
			isSent: false,
		});

		Session.setFormStateDirty(true);
	};

	onFieldBlur = e => {
		const { name, value } = e.target;

		// validate the field
		this.validate(name, value);
	};

	onSendButtonClick = async () => {
		const {
			yourName,
			firstName,
			senderAddress,
			senderCity,
			senderState,
			senderZip,
			recipientEmail,
			message,
			friendCode,
		} = this.state;
		const {
			modalContext: { showError },
		} = this.props;

		const { MerchantType } = Session.getSettings();
		const parsedEmails = recipientEmail
			.split(',')
			.map(email => email.trim())
			.join(',');

		// SendGrid requires a comma separated name value tied to each email
		const firstNameCsv = parsedEmails
			.split(',')
			.map(email => firstName)
			.join(',');

		const emailParams = {
			TemplateName: 'referAFriend',
			NvPairs: {
				merchant: MerchantType,
				yourName,
				merchantUrl: 'stamps.com',
				senderAddress,
				senderCity,
				senderState,
				senderZip,
				strEmailAddress: parsedEmails,
				strFirstName: firstNameCsv,
				subject: `${yourName} wants you to try Stamps.com`,
				referralUrl: `https://www.stamps.com/tellafriend/landing/?source=si10190196&PromoCode=${friendCode}`,
				message,
				unsubscribeUrl: 'https://www.stamps.com/unsubscribe',
				cdnPath: 'https://stampswebsite.blob.core.windows.net/assets/',
				currentYear: new Date().getFullYear(),
				friendCode,
			},
		};

		// validate all fields
		const validationResult = this.validate();

		Logger.saveActionData('Send Refer A Friend Button Clicked');

		// show 'Incomplete Fields' modal if any field is invalid
		if (includes(validationResult.validations, -1)) {
			return showError({
				data: {
					title: 'Incomplete Fields',
					description: Validation.buildValidationErrorDescription(
						validationResult.errorMessages
					),
					primaryButtonType: ModalButtonActionType.Ok,
					primaryButtonText: 'Close',
					handleType: 'custom',
				},
			});
		}

		const response = await AccountCommunication.sendEmail(emailParams);

		if (AccountCommunication.isSuccessful(response)) {
			this.setState({
				clean: true,
				isSent: true, // per WEBAPPS-25781, show a success confirmation
				recipientEmail: '', // per WEBAPPS-25781, should empty the email field after sending the emails
				validations: {
					...validationResult.validations,
					recipientEmail: 0, // reset the email validation to untouched
				},
			});

			Session.setFormStateDirty(false);
		} else {
			showError({ response });
		}
	};

	launchTermsModal = () => {
		const {
			modalContext: { showModal },
		} = this.props;

		showModal({
			component: TermsModal,
		});
	};

	copyToClipboard = text => {
		navigator.clipboard.writeText(text);
		this.setState({ copied: true });
		setTimeout(() => {
			this.setState({ copied: false });
		}, 2000);
	};

	render() {
		const {
			friendCode,
			clean,
			yourName,
			message,
			recipientEmail,
			validations,
			errorMessages,
			isSent,
		} = this.state;
		const {
			layoutContext: { isMobile },
		} = this.props;

		const placeholder =
			'Please enter the e-mail addresses separated by a comma. For example: jane@example.com, joe@example.com';

		return (
			<div className="account-refer-a-friend">
				<Section>
					<SectionBody>
						<div className="body">
							{isSent && (
								// TODO: update text to something that makes more sense for sending an email
								<SaveConfirmation
									success
									text="Your changes have been saved."
								/>
							)}
							<h2 className="primary-header">
								Refer anyone to Stamps.com and Receive $20 in Free Postage!
							</h2>
							<p className="description">
								Know somebody who can benefit from using Stamps.com? Then let
								them know. Every time you refer someone to Stamps.com and they
								remain a customer past the trial period for 90 days, you will
								earn a $20 free postage reward. Of course, your friends will
								benefit too with a valuable $100 offer that includes postage and
								a digital scale.
							</p>
							<h3 className="secondary-header">3 ways to Refer-A-Friend:</h3>
							<div className="item-list">
								<div className="item item-block">
									<div>
										<p>
											<span className="item-letter">a.</span>
											<span className="promo-code-text">
												Share your unique promo code with friends and have them
												enter it at{' '}
												<a
													role="button"
													target="_blank"
													tabIndex="0"
													href={`https://www.stamps.com/tell`}
													rel="noreferrer"
												>
													www.stamps.com/tell.
												</a>{' '}
											</span>
										</p>
									</div>
									<div className="promo-code">
										<div className="grid">
											<div className="p-col-12">
												<p className="promo-text">
													Your Tell-A-Friend Promo Code is:{' '}
												</p>
											</div>
											<div className="p-col-12">
												<p
													className="friend-code copy-icon"
													onClick={() => this.copyToClipboard(friendCode)}
												>
													{friendCode}
												</p>
												{this.state.copied && (
													<span className="copied-text">Copied!</span>
												)}
											</div>
										</div>
									</div>
								</div>
								<div className="item share item-block">
									<div className="grid">
										<div className="p-col-12">
											<p className="share-text promo-code-text">
												<span className="item-letter">b.</span>
												<b>Share via:</b>
											</p>
										</div>
										<div className="p-col-12 share-buttons">
											<div className="facebook-wrapper">
												<div id="fb-root" />
												<AdlSocialButton
													platform="facebook"
													onClick={this.onShareFacebookClick}
													dataqa="share-facebook"
												/>
											</div>
											<AdlSocialButton
												platform="x"
												onClick={this.onShareTwitterClick}
												dataqa="share-x"
											/>
										</div>
									</div>
								</div>

								<div className="item item-block">
									<p className="promo-code-text">
										<span className="item-letter">c.</span>
										<b>Send them an email below:</b>
									</p>
									<div className="email-wrapper">
										<WrappedInputTextWithLabel
											isInputInvalid={validations.yourName === -1}
											showErrorBelow={isMobile}
											errorMessage={errorMessages.yourName}
											className="p-col-12 p-md-8"
											label="Your Name:"
											uiElementConfig={{
												onChange: this.onFieldChange,
												onBlur: this.onFieldBlur,
												maxLength: 100,
												value: yourName,
												dataqa: 'yourname',
												name: 'yourName',
											}}
										/>
										<WrappedInputTextareaWithLabel
											isInputInvalid={validations.message === -1}
											showErrorBelow={isMobile}
											errorMessage={errorMessages.message}
											className="p-col-12 p-md-8"
											label="Message:"
											uiElementConfig={{
												rows: 5,
												cols: 41,
												maxLength: 250,
												value: message,
												onChange: this.onFieldChange,
												onBlur: this.onFieldBlur,
												dataqa: 'message',
												name: 'message',
											}}
										/>

										<WrappedInputTextareaWithLabel
											isInputInvalid={validations.recipientEmail === -1}
											showErrorBelow={isMobile}
											errorMessage={errorMessages.recipientEmail}
											className="p-col-12 p-md-8"
											label="Recipient Email Address:"
											uiElementConfig={{
												rows: 5,
												cols: 41,
												value: recipientEmail,
												onChange: this.onFieldChange,
												onBlur: this.onFieldBlur,
												placeholder,
												dataqa: 'recipientemail',
												name: 'recipientEmail',
											}}
										/>
									</div>
								</div>

								<div className="terms">
									View the{' '}
									<a role="button" tabIndex="0" onClick={this.launchTermsModal}>
										Terms and Conditions
									</a>{' '}
									that govern your participation in this program.
								</div>
							</div>
						</div>
					</SectionBody>
					<Footer
						primary={{
							label: 'Send',
							disabled: clean,
							onClick: this.onSendButtonClick,
							dataqa: 'refer-a-friend-send',
						}}
						className="account-footer"
					/>
				</Section>
			</div>
		);
	}
}

export default withLayoutAndModalContexts(ReferAFriend);
